import React, { createContext, useContext, useState, useEffect } from 'react';

const InstructorContext = createContext();

export const useInstructor = () => useContext(InstructorContext);

export const InstructorProvider = ({ children }) => {
    const [students, setStudents] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [branches, setBranches] = useState([]);
    const [instructorProfile, setInstructorProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const config = window.reactPress || window.rpVars || {};
    const API_BASE = (config.api?.rest_url || '/wp-json').replace(/\/$/, '') + '/sdt/v1';
    const nonce = config.api?.nonce || config.nonce;
    const headers = { 'X-WP-Nonce': nonce, 'Content-Type': 'application/json' };

    const fetchData = async () => {
        try {
            const [custRes, lessonsRes, instructorRes, branchesRes] = await Promise.all([
                fetch(`${API_BASE}/customer`, { headers }),
                fetch(`${API_BASE}/lessons`, { headers }),
                fetch(`${API_BASE}/instructor`, { headers }),
                fetch(`${API_BASE}/branches`, { headers })
            ]);

            if (custRes.ok) {
                const custData = await custRes.json();
                const allLessons = lessonsRes.ok ? await lessonsRes.json() : [];
                setSchedule(allLessons);

                setStudents(custData.map(c => {
                    const studentLessons = allLessons.filter(l => l.customer_id === c.customer_id);
                    const completed = studentLessons.filter(l => l.lesson_status === 'Completed' || parseInt(l.student_completed) === 1).length;
                    const total = completed + parseInt(c.lessons_remaining || 0);
                    return {
                        ...c,
                        name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown Student',
                        progress: total > 0 ? Math.round((completed / total) * 100) : 0,
                        lessons_completed: completed,
                        avatar: (c.first_name?.[0] || '') + (c.last_name?.[0] || '')
                    };
                }));
            } else if (lessonsRes.ok) {
                const allLessons = await lessonsRes.json();
                setSchedule(allLessons);
            }


            if (branchesRes.ok) {
                const branchesData = await branchesRes.json();
                setBranches(branchesData);
            }

            // Get logged-in user's instructor profile
            if (instructorRes.ok) {
                const instructorData = await instructorRes.json();
                // For now, use the first instructor or find by user_id if available
                const instructor = instructorData.find(s => s.role === 'Instructor') || instructorData[0];
                if (instructor) {
                    setInstructorProfile(instructor);
                }
            }
        } catch (error) {
            console.error('Error fetching Instructor data:', error);
        } finally {
            setLoading(false);
        }
    };

    const createLesson = async (lessonData) => {
        try {
            // Automatically add instructor_id if available
            const payload = {
                ...lessonData,
                instructor_id: instructorProfile?.instructor_id || instructorProfile?.id || null
            };
            const response = await fetch(`${API_BASE}/lessons`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || 'Failed to create lesson');
            }
            await fetchData(); // Refresh data
            return await response.json();
        } catch (error) {
            console.error('Error creating lesson:', error);
            throw error;
        }
    };

    useEffect(() => {
        fetchData();
    }, [nonce]);

    return (
        <InstructorContext.Provider value={{
            students,
            schedule,
            branches,
            instructorProfile,
            instructorBranchId: instructorProfile?.branch_id || null,
            loading,
            refetch: fetchData,
            createLesson
        }}>
            {children}
        </InstructorContext.Provider>
    );
};
